/**
 * Il servizio della pagina console «Anagrafe componenti».
 *
 * Legge e cura `catalog_components` e il registro `marchi` col client SDK: la scrittura è
 * concessa dalle rules SOLO al super utente (i tenant non scrivono mai qui — la garanzia
 * resta). I media universali salgono su Storage in `catalog-components/{id}/…`, un path
 * dedicato all'anagrafe: mai mischiati coi file dei tenant.
 *
 * ⚠️ Chi scrive, rilegge (regola #1-bis): le funzioni di salvataggio tornano il documento
 * riletto, non il patch che credono di aver scritto.
 */
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { db, storage } from '../firebase';
import { isUnitaMisuraBase, normalizzaMpn, normalizzaNomeMarchio } from '@shared/anagrafe/identificativi';
import type { ComponenteCanonico, ConfezioneAnagrafe, Marchio, MediaAnagrafe, MediaTipoAnagrafe, RegimeMatricoleConsole } from '../types/anagrafe';
import { REGIMI_MATRICOLE_CONSOLE } from '../types/anagrafe';

const COMPONENTI = 'catalog_components';
const MARCHI = 'marchi';

function daDocumento(id: string, p: Record<string, unknown>): ComponenteCanonico {
  const confezioni = Array.isArray(p.confezioni)
    ? (p.confezioni as unknown[])
        .filter((c): c is Record<string, unknown> => !!c && typeof c === 'object')
        .map((c) => ({
          nome: typeof c.nome === 'string' ? c.nome : '',
          pezzi: typeof c.pezzi === 'number' ? c.pezzi : Number(c.pezzi) || 0,
          ...(typeof c.gtin === 'string' && c.gtin ? { gtin: c.gtin } : {}),
        }))
    : [];
  return {
    id,
    categoria_code: typeof p.categoria_code === 'string' ? p.categoria_code : '',
    costruttore: typeof p.costruttore === 'string' ? p.costruttore : null,
    modello: typeof p.modello === 'string' ? p.modello : null,
    codice_costruttore: typeof p.codice_costruttore === 'string' ? p.codice_costruttore : null,
    specs: p.specs && typeof p.specs === 'object' ? (p.specs as Record<string, unknown>) : {},
    logo_url: typeof p.logo_url === 'string' ? p.logo_url : undefined,
    media: Array.isArray(p.media) ? (p.media as MediaAnagrafe[]) : undefined,
    origine_prodotti: Array.isArray(p.origine_prodotti) ? (p.origine_prodotti as string[]) : [],
    attivo: p.attivo !== false,
    marchio_id: typeof p.marchio_id === 'string' ? p.marchio_id : null,
    marchio_da_assegnare: p.marchio_da_assegnare === true,
    marchio_nota: typeof p.marchio_nota === 'string' ? p.marchio_nota : undefined,
    mpn_normalizzato: typeof p.mpn_normalizzato === 'string' ? p.mpn_normalizzato : normalizzaMpn(typeof p.codice_costruttore === 'string' ? p.codice_costruttore : null),
    gtin: Array.isArray(p.gtin) ? (p.gtin as unknown[]).filter((g): g is string => typeof g === 'string') : [],
    unita_misura_base: isUnitaMisuraBase(p.unita_misura_base) ? p.unita_misura_base : 'pz',
    unita_misura_origine: p.unita_misura_origine === 'confermata' ? 'confermata' : 'dedotta',
    confezioni,
    matricole: (REGIMI_MATRICOLE_CONSOLE as readonly unknown[]).includes(p.matricole) ? (p.matricole as RegimeMatricoleConsole) : undefined,
    formato_matricola: typeof p.formato_matricola === 'string' && p.formato_matricola ? p.formato_matricola : undefined,
    classe_etim: typeof p.classe_etim === 'string' && p.classe_etim ? p.classe_etim : undefined,
    stato_curatela: p.stato_curatela === 'proposto' ? 'proposto' : 'verificato',
    proposto_da_tenant: typeof p.proposto_da_tenant === 'string' ? p.proposto_da_tenant : undefined,
    fuso_in: typeof p.fuso_in === 'string' ? p.fuso_in : undefined,
  };
}

function marchioDaDocumento(id: string, p: Record<string, unknown>): Marchio {
  return {
    id,
    nome_canonico: typeof p.nome_canonico === 'string' ? p.nome_canonico : id.toUpperCase(),
    alias: Array.isArray(p.alias) ? (p.alias as unknown[]).filter((a): a is string => typeof a === 'string') : [],
    logo_url: typeof p.logo_url === 'string' ? p.logo_url : null,
    formato_matricola: typeof p.formato_matricola === 'string' && p.formato_matricola ? p.formato_matricola : undefined,
    attivo: p.attivo !== false,
    origine: p.origine === 'manuale' || p.origine === 'brands' ? p.origine : 'anagrafe',
  };
}

export async function caricaAnagrafe(): Promise<ComponenteCanonico[]> {
  const snap = await getDocs(collection(db, COMPONENTI));
  const out: ComponenteCanonico[] = [];
  snap.forEach((d) => out.push(daDocumento(d.id, d.data())));
  out.sort((a, b) => (a.costruttore ?? '').localeCompare(b.costruttore ?? '') || (a.modello ?? '').localeCompare(b.modello ?? ''));
  return out;
}

export async function caricaMarchi(): Promise<Marchio[]> {
  const snap = await getDocs(collection(db, MARCHI));
  const out: Marchio[] = [];
  snap.forEach((d) => out.push(marchioDaDocumento(d.id, d.data())));
  out.sort((a, b) => a.nome_canonico.localeCompare(b.nome_canonico));
  return out;
}

async function rileggiComponente(id: string): Promise<ComponenteCanonico> {
  const snap = await getDoc(doc(db, COMPONENTI, id));
  if (!snap.exists()) throw new Error(`Il componente ${id} non esiste più`);
  return daDocumento(snap.id, snap.data());
}

export type PatchComponente = Partial<
  Pick<
    ComponenteCanonico,
    | 'costruttore'
    | 'modello'
    | 'codice_costruttore'
    | 'specs'
    | 'logo_url'
    | 'media'
    | 'attivo'
    | 'marchio_id'
    | 'gtin'
    | 'unita_misura_base'
    | 'confezioni'
    | 'matricole'
    | 'formato_matricola'
    | 'classe_etim'
    | 'stato_curatela'
  >
>;

/**
 * Aggiorna il componente (i campi passati, nient'altro) e lo RILEGGE.
 * Regole di coerenza scritte qui, una volta: il codice normalizzato segue sempre il codice
 * del costruttore; l'unità scelta a mano diventa `confermata`; assegnare un marchio toglie il
 * segnale «da assegnare» e allinea `costruttore` al nome canonico; un override di matricola
 * o di formato lasciato vuoto si CANCELLA (null), non resta appeso.
 */
export async function salvaComponente(id: string, patch: PatchComponente, marchi?: Marchio[]): Promise<ComponenteCanonico> {
  const scrittura: Record<string, unknown> = { ...patch, updated_at: serverTimestamp() };
  if ('codice_costruttore' in patch) scrittura.mpn_normalizzato = normalizzaMpn(patch.codice_costruttore ?? null);
  if ('unita_misura_base' in patch) scrittura.unita_misura_origine = 'confermata';
  if ('marchio_id' in patch) {
    if (patch.marchio_id) {
      const m = marchi?.find((x) => x.id === patch.marchio_id);
      if (m) scrittura.costruttore = m.nome_canonico;
      scrittura.marchio_da_assegnare = false;
      scrittura.marchio_nota = null;
    }
  }
  if ('matricole' in patch && patch.matricole === undefined) scrittura.matricole = null;
  if ('formato_matricola' in patch && !patch.formato_matricola) scrittura.formato_matricola = null;
  if ('classe_etim' in patch && !patch.classe_etim) scrittura.classe_etim = null;
  if ('confezioni' in patch) {
    scrittura.confezioni = (patch.confezioni ?? [])
      .filter((c: ConfezioneAnagrafe) => c.nome.trim() !== '' && c.pezzi > 0)
      .map((c: ConfezioneAnagrafe) => ({ nome: c.nome.trim(), pezzi: c.pezzi, ...(c.gtin ? { gtin: c.gtin } : {}) }));
  }
  await updateDoc(doc(db, COMPONENTI, id), scrittura);
  return rileggiComponente(id);
}

/**
 * Salva un marchio del registro e lo RILEGGE. Se cambia il nome canonico, il nuovo nome
 * si propaga a `costruttore` di tutti i componenti collegati (è una cache del nome, non una
 * seconda verità): un batch solo, così o cambia per tutti o per nessuno.
 */
export async function salvaMarchio(
  id: string,
  patch: Partial<Pick<Marchio, 'nome_canonico' | 'alias' | 'formato_matricola' | 'attivo' | 'logo_url'>>,
): Promise<{ marchio: Marchio; componenti_rinominati: number }> {
  const scrittura: Record<string, unknown> = { ...patch, origine: 'manuale', updated_at: serverTimestamp() };
  if ('formato_matricola' in patch && !patch.formato_matricola) scrittura.formato_matricola = null;
  if (patch.alias) scrittura.alias = [...new Set(patch.alias.map(normalizzaNomeMarchio).filter(Boolean))].sort();
  let rinominati = 0;
  if (patch.nome_canonico) {
    const nome = patch.nome_canonico.trim();
    scrittura.nome_canonico = nome;
    scrittura.alias = [...new Set([...(scrittura.alias as string[] | undefined) ?? [], normalizzaNomeMarchio(nome)].filter(Boolean))].sort();
    const collegati = await getDocs(query(collection(db, COMPONENTI), where('marchio_id', '==', id)));
    const batch = writeBatch(db);
    collegati.forEach((d) => {
      if (d.data().costruttore !== nome) {
        batch.update(d.ref, { costruttore: nome, updated_at: serverTimestamp() });
        rinominati += 1;
      }
    });
    batch.update(doc(db, MARCHI, id), scrittura);
    await batch.commit();
  } else {
    await updateDoc(doc(db, MARCHI, id), scrittura);
  }
  const snap = await getDoc(doc(db, MARCHI, id));
  if (!snap.exists()) throw new Error(`Il marchio ${id} non esiste più`);
  return { marchio: marchioDaDocumento(snap.id, snap.data()), componenti_rinominati: rinominati };
}

/**
 * Carica un file media su Storage e ritorna la voce pronta per il documento.
 * Il nome file conserva l'estensione originale; il tipo fa da prefisso così la
 * cartella del componente si legge da sola.
 */
export async function caricaMediaSuStorage(
  componenteId: string,
  tipo: MediaTipoAnagrafe,
  file: File,
): Promise<MediaAnagrafe> {
  const pulito = file.name.replace(/[^\w.\-]+/g, '_');
  const path = `catalog-components/${componenteId}/${tipo}-${pulito}`;
  const r = ref(storage, path);
  await uploadBytes(r, file, { contentType: file.type || undefined });
  const url = await getDownloadURL(r);
  return { tipo, download_url: url, storage_path: path, nome: file.name };
}

/**
 * L'array media aggiornato con la voce nuova: texture e modello 3D sono UNICI per
 * componente (la nuova sostituisce la vecchia), documenti e immagini si accumulano.
 */
export function mediaAggiornati(attuali: MediaAnagrafe[] | undefined, nuovo: MediaAnagrafe): MediaAnagrafe[] {
  const unici: MediaTipoAnagrafe[] = ['texture', 'modello_3d'];
  const base = attuali ?? [];
  return unici.includes(nuovo.tipo) ? [...base.filter((m) => m.tipo !== nuovo.tipo), nuovo] : [...base, nuovo];
}
